import { Card, CardVariant } from '@components/card';
import { DesignAvatar } from '@components/design-avatar';
import { HideContainer } from '@components/hide-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconLoopRestart } from '@components/icons/loop-restart';
import { IconTrash } from '@components/icons/trash';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { ActionChange, ActionModification } from 'api/types';
import { ActionIcon, getActionLabel } from 'ui/actions/action-config';

const ActionCard = ({ action }: { action: ActionChange | ActionModification }) => {
  const renderIcon = () => {
    if (action.logo) {
      return <SquareImage src={action.logo} />;
    }
    if (action.avatar) {
      return <DesignAvatar src={action.avatar} size={20} />;
    }
    return <ActionIcon action={action.action} />;
  };

  const label = action.name ?? getActionLabel(action.action);

  return (
    <Stack orientation="horizontal" space={2} align="center">
      {renderIcon()}
      <div>
        <Text font="semibold12" text={label} />
        {action.description && (
          <Text font="regular12" color="textSecondary" text={action.description} />
        )}
      </div>
    </Stack>
  );
};

export type ActionChangesSectionProps = {
  summary: { added: number; modified: number; removed: number };
  addedActions: ActionChange[];
  modifiedActions: ActionModification[];
  removedActions: ActionChange[];
};

export const ActionChangesSection = ({
  summary,
  addedActions,
  modifiedActions,
  removedActions,
}: ActionChangesSectionProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={3}>
      {summary.added > 0 && (
        <HideContainer
          title={t('actionsAdded')}
          icon={<IconAddPlus color="elementGreen100" />}
          counter={summary.added}
        >
          <Stack space={2}>
            {addedActions.map((action) => (
              <SummaryAction action={action} key={action.shortId} variant="green" />
            ))}
          </Stack>
        </HideContainer>
      )}
      {summary.modified > 0 && (
        <HideContainer
          title={t('actionsModified')}
          icon={<IconLoopRestart color="elementOrange100" />}
          counter={summary.modified}
        >
          <Stack space={2}>
            {modifiedActions.map((action) => (
              <SummaryAction action={action} key={action.shortId} variant="orange" />
            ))}
          </Stack>
        </HideContainer>
      )}
      {summary.removed > 0 && (
        <HideContainer
          title={t('actionsRemoved')}
          icon={<IconTrash color="elementRed100" />}
          counter={summary.removed}
        >
          <Stack space={2}>
            {removedActions.map((action) => (
              <SummaryAction action={action} key={action.shortId} variant="red" />
            ))}
          </Stack>
        </HideContainer>
      )}
    </Stack>
  );
};

const SummaryAction = ({
  action,
  variant,
}: {
  action: ActionChange | ActionModification;
  variant: CardVariant;
}) => {
  return (
    <Card variant={variant} spaceX={3} spaceY={4} key={action.shortId}>
      <ActionCard action={action} />
    </Card>
  );
};

const locale = {
  en: {
    actionsAdded: 'Actions added',
    actionsModified: 'Actions modified',
    actionsRemoved: 'Actions removed',
  },
};
